import { useMemo, useRef, useState } from "react";
import type { LogEntry } from "../../core/eventLog";
import { formatRelativeTime, kindBadgeClass, truncatePreview } from "../format";
import {
  distinctContactIds,
  distinctModuleIds,
  filterEntries,
  type TimelineFilters,
} from "../timelineFilter";

type Props = {
  entries: readonly LogEntry[];
  selectedSeq: number | undefined;
  onSelect: (seq: number) => void;
};

const RENDER_CAP = 500;

export function TimelinePanel({
  entries,
  selectedSeq,
  onSelect,
}: Props): React.JSX.Element {
  const [filters, setFilters] = useState<TimelineFilters>({});
  const [paused, setPaused] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  const moduleIds = useMemo(() => distinctModuleIds(entries), [entries]);
  const contactIds = useMemo(() => distinctContactIds(entries), [entries]);

  const filtered = useMemo(
    () => filterEntries(entries, filters),
    [entries, filters],
  );
  // Newest first.
  const ordered = useMemo(() => [...filtered].reverse(), [filtered]);
  const visible = ordered.slice(0, RENDER_CAP);

  const handleScroll = (): void => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop > 8 && !paused) setPaused(true);
  };

  const resume = (): void => {
    setPaused(false);
    listRef.current?.scrollTo({ top: 0 });
  };

  return (
    <div className="timeline-panel">
      <div className="timeline-filters">
        <select
          aria-label="Filter by module"
          value={filters.moduleId ?? ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, moduleId: e.target.value || undefined }))
          }
        >
          <option value="">All modules</option>
          {moduleIds.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by kind"
          value={filters.kind ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              kind: (e.target.value || undefined) as TimelineFilters["kind"],
            }))
          }
        >
          <option value="">All kinds</option>
          {["event", "request", "error", "lifecycle", "log", "interceptor"].map(
            (k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ),
          )}
        </select>
        <select
          aria-label="Filter by contact"
          value={filters.contactId ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              contactId: e.target.value || undefined,
            }))
          }
        >
          <option value="">All contacts</option>
          {contactIds.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search…"
          aria-label="Free text filter"
          value={filters.text ?? ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, text: e.target.value || undefined }))
          }
        />
        {paused ? (
          <button type="button" onClick={resume} className="resume-btn">
            Resume auto-scroll (paused)
          </button>
        ) : null}
      </div>

      {ordered.length === 0 ? (
        <p className="empty-state">No events captured yet.</p>
      ) : (
        <div className="timeline-list" ref={listRef} onScroll={handleScroll}>
          {visible.map((e) => (
            <button
              type="button"
              key={e.seq}
              className={`timeline-row ${selectedSeq === e.seq ? "selected" : ""}`}
              onClick={() => onSelect(e.seq)}
            >
              <span className="tl-seq">#{e.seq}</span>
              <span className="tl-time">{formatRelativeTime(e.t)}</span>
              <span className={`badge ${kindBadgeClass(e.kind)}`}>
                {e.kind}
              </span>
              <span className="tl-name">{`${e.moduleId}.${e.name}`}</span>
              <span className="tl-contact">{e.contactId ?? ""}</span>
              <span className="tl-preview">{truncatePreview(e.payload)}</span>
            </button>
          ))}
          {ordered.length > RENDER_CAP ? (
            <p className="timeline-truncated">
              Showing newest {RENDER_CAP} of {ordered.length} matching entries.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
